import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/common/Button';
import { useAuth } from '../hooks/useAuth';
import './LandingPage.css';

interface FaqItem {
  /** Âncora para link direto (ex.: /faq#salva-senhas abre esta pergunta). */
  id?: string;
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    question: 'O que é o Lictor?',
    answer:
      'O Lictor é uma plataforma de gerenciamento centralizado de credenciais criada para escritórios de advocacia — mas que funciona com qualquer website que exija login. Ela permite que toda a sua equipe acesse sistemas, portais e tribunais de forma segura, sem que as senhas precisem ser compartilhadas individualmente.',
  },
  {
    question: 'O Lictor funciona apenas com tribunais?',
    answer:
      'Não! O Lictor é ESPECIALISTA em tribunais (com preenchimento otimizado para PJe, Eproc, e-SAJ e Projudi), mas a extensão funciona como um cofre universal para qualquer sistema. Você pode cadastrar e usar credenciais de sistemas de clientes, ferramentas de trabalho, intranets, ERPs e muito mais — tudo centralizado no mesmo cofre.',
  },
  {
    question: 'Como o Lictor mantém minhas senhas seguras?',
    answer:
      'Cada credencial é cifrada com AES-256, com uma chave própria, e essa chave é protegida pelo AWS KMS, o serviço de chaves da Amazon. Assim, uma cópia do banco de dados sozinha não basta para ler nenhuma senha. A senha só é decifrada no momento do uso, para quem tem permissão: membros usam a credencial sem que a senha apareça na tela, e cada uso fica no registro de atividades do escritório.',
  },
  {
    question: 'O que é a extensão de navegador e preciso instalá-la?',
    answer:
      'A extensão é um componente opcional que permite o preenchimento automático de login nos portais dos tribunais. Com ela instalada, o colaborador visita o site do tribunal e as credenciais são inseridas automaticamente, sem que ele precise digitar ou ver a senha.',
  },
  {
    question: 'O que é o 2FA (TOTP) integrado?',
    answer:
      'Muitos tribunais exigem um código de segundo fator (2FA) no login. O Lictor gerencia esses códigos de forma centralizada, eliminando a necessidade de o colaborador ter acesso ao celular do sócio para buscar um código de autenticação.',
  },
  {
    question: 'Posso controlar quem tem acesso a quais credenciais?',
    answer:
      'Sim. O Lictor possui um sistema de controle de acessos por grupos. Você pode, por exemplo, criar o grupo "Equipe Trabalhista" e dar acesso apenas às credenciais dos tribunais trabalhistas. Cada membro só consegue usar as credenciais do seu grupo.',
  },
  {
    question: 'O que é o registro de atividades?',
    answer:
      'É o histórico das ações feitas no Lictor: quem usou, viu, criou ou alterou cada credencial, quando e de onde. Administradores e proprietários do escritório consultam esse histórico no painel, com filtros por pessoa, credencial e período.',
  },
  {
    question: 'Posso adicionar mais de 10 usuários ao plano?',
    answer:
      'Sim. O plano padrão inclui até 10 usuários ativos por R$ 299/mês. Para cada usuário adicional, há uma cobrança de R$ 10/mês. Para escritórios com mais de 100 colaboradores, entre em contato para um plano customizado.',
  },
  {
    question: 'O Lictor funciona com todos os tribunais e sistemas?',
    answer:
      'A nossa extensão é ESPECIALISTA nos principais portais de tribunais brasileiros (preenchimento cirúrgico e auto-submissão). No entanto, o Lictor atua como um cofre universal: ele funciona com qualquer website ou sistema que possua uma tela de login. Você pode utilizá-lo perfeitamente em sistemas de clientes, ferramentas de trabalho, intranets, portais de governo e ERPs. É um único cofre para todas as senhas do seu escritório.',
  },
  {
    question: 'Como funciona o suporte?',
    answer:
      'Oferecemos suporte via e-mail para todos os clientes. Planos empresariais possuem canal de atendimento prioritário. Você pode entrar em contato a qualquer momento pelo e-mail lictor.suporte@gmail.com.',
  },
  {
    question: 'Posso cancelar a qualquer momento?',
    answer:
      'Sim. Não há fidelidade ou multa de cancelamento. Você pode cancelar a sua assinatura a qualquer momento pelo painel de configurações.',
  },
  {
    question: 'Como funciona a extensão de preenchimento automático?',
    answer:
      'Atualmente, a extensão do Lictor está em versão Beta e disponível exclusivamente na Chrome Web Store. Para a melhor experiência possível e para evitar conflitos, recomendamos fortemente que você utilize o navegador Google Chrome e desabilite o gerenciador/preenchimento nativo de senhas do próprio navegador.',
  },
  {
    id: 'salva-senhas',
    question: 'Como desativo o salva-senhas do navegador?',
    answer:
      'Chrome e Edge: Configurações → Senhas (ou Gerenciador de senhas) → desative "Oferecer para salvar senhas" e "Login automático". Firefox: Configurações → Privacidade e Segurança → Logins e senhas → desmarque "Pedir para salvar logins e senhas de sites". Assim o navegador não preenche senhas antigas por cima das que o Lictor guarda.',
  },
  {
    question: 'Como crio uma nova credencial da maneira correta?',
    answer:
      'Criar uma credencial no Lictor é extremamente simples. No painel, você só precisa preencher a URL (endereço) do site de login, o seu usuário, a sua senha e escanear o QR Code de 2FA (ou colar o código) se o site exigir segundo fator de autenticação. Com essas informações, a nossa extensão fará a mágica de preencher os seus dados automaticamente de forma perfeita quando você acessar o site.',
  },
];

function FaqAccordion({ item }: { item: FaqItem }) {
  const location = useLocation();
  const [open, setOpen] = useState(() => !!item.id && location.hash === `#${item.id}`);

  return (
    <div
      id={item.id}
      style={{
        borderBottom: '1px solid var(--color-border-subtle)',
        padding: 'var(--space-5) 0',
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: 'var(--space-4)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-text-primary)',
          }}
        >
          {item.question}
        </span>
        {open ? (
          <ChevronUp size={20} style={{ flexShrink: 0, color: 'var(--color-primary-400)' }} />
        ) : (
          <ChevronDown size={20} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
        )}
      </button>

      {open && (
        <p
          style={{
            marginTop: 'var(--space-3)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.7,
            fontSize: 'var(--text-sm)',
          }}
        >
          {item.answer}
        </p>
      )}
    </div>
  );
}

export default function FAQ() {
  const { isAuthenticated } = useAuth();
  usePageTitle('Perguntas frequentes');
  return (
    <div className="landing-container">
      {/* Navbar */}
      <header className="landing-header">
        <div className="landing-nav">
          <Link to="/" className="landing-logo" style={{ textDecoration: 'none' }}>
            <img src="/logo.png" alt="Lictor" className="landing-logo-icon" style={{ width: 48, height: 48 }} />
            <span>Lictor</span>
          </Link>
          <div className="landing-nav-ctas">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button variant="primary" size="sm">Acessar Painel</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">Entrar</Button>
                </Link>
                <Link to="/register">
                  <Button variant="primary" size="sm">Criar Conta</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '760px', margin: '0 auto', padding: 'var(--space-16) var(--space-6)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-12)' }}>
          <span className="landing-section-subtitle">Suporte</span>
          <h1
            style={{
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text-primary)',
              marginTop: 'var(--space-2)',
              marginBottom: 'var(--space-4)',
            }}
          >
            Perguntas Frequentes
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-base)', lineHeight: 1.6 }}>
            Não encontrou o que procurava? Entre em contato pelo{' '}
            <a
              href="mailto:lictor.suporte@gmail.com"
              style={{ color: 'var(--color-primary-400)', textDecoration: 'none' }}
            >
              lictor.suporte@gmail.com
            </a>
          </p>
        </div>

        {/* FAQ Items */}
        <div>
          {faqItems.map((item, i) => (
            <FaqAccordion key={i} item={item} />
          ))}
        </div>

        {/* CTA */}
        <div
          style={{
            marginTop: 'var(--space-16)',
            textAlign: 'center',
            padding: 'var(--space-10)',
            background: 'var(--color-bg-surface)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--color-border-subtle)',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-3)',
            }}
          >
            Pronto para começar?
          </h2>
          <p
            style={{
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--space-6)',
            }}
          >
            Crie sua conta e comece a usar o Lictor hoje mesmo.
          </p>
          <Link to="/register">
            <Button variant="primary" size="lg">Criar Minha Conta</Button>
          </Link>
        </div>
      </main>

      <footer className="landing-footer" style={{ marginTop: 'var(--space-12)' }}>
        <div className="landing-footer-bottom">
          <div className="landing-copyright">
            &copy; {new Date().getFullYear()} Lictor. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
